/**
 * Professional type constants for the Belagom client.
 * Must be kept in sync with src/types/professionalRates.ts (server).
 */

export type ProfessionalType =
  // Legacy (backwards compat with existing profiles)
  | 'loopbaancoach'
  | 'vitaliteitscoach'
  | 'burnout_coach'
  | 'financieel_coach'
  | 'psycholoog_coach'
  | 'supervisor'
  | 'bedrijfspsycholoog_preventief'
  // Categorie 1: Ontwikkeling & Groei (L&D)
  | 'executive_leiderschapscoach'
  | 'business_coach'
  | 'teamcoach'
  | 'agile_performance_coach'
  | 'ld_business_partner'
  | 'change_verandermanager'
  | 'talent_high_potential_coach'
  | 'ld_adviseur_trainer'
  | 'arbeidsdeskundige'
  // Categorie 2: Psychosociale Arbeidsbelasting (PSA)
  | 'mediator'
  | 'vertrouwenspersoon'
  | 'intercultureel_coach'
  | 'psa_adviseur_burnout_coach'
  | 'casemanager_inzetbaarheid'
  | 'casemanager_regie_verzuim'
  | 'bedrijfspsycholoog'
  | 'bedrijfsarts_arboarts'
  // Categorie 3: Welzijn & Vitaliteit
  | 'organisatiepsycholoog'
  | 'vitaliteitsmanager_coach'
  | 'loopbaan_inzetbaarheidscoach'
  | 'budgetcoach_financieel_welzijn'
  | 'natuur_wandelcoach'
  | 'ergonomisch_adviseur'
  | 'verslavingscoach'
  | 'arbeidshygienist'
  | 'arboverpleegkundige'
  | 'ergotherapeut_reintegratiecoach'
  // Categorie 4: Trauma & Incidenten
  | 'crisis_trauma_specialist'
  | 'rouw_verliesbegeleider'
  | 'bedrijfsmaatschappelijk_werker'
  // Categorie 1 (aanvulling): Ontwikkeling & Groei
  | 'spoor2_reintegratiespecialist'
  // Categorie 2 (aanvulling): PSA
  | 'conflictcoach'
  // Categorie 5: Arbo, Keuringen & Verzuimregie
  | 'praktijkondersteuner_bedrijfsarts'
  | 'hogere_veiligheidskundige'
  // Categorie 3 (aanvulling): Welzijn & Vitaliteit
  | 'arbeidsfysiotherapeut'
  // Categorie 2 (aanvulling): PSA / organisatiebeleid
  | 'arbeids_organisatie_deskundige'
  // Uitbreiding registerblauwdruk (migratie 172)
  | 'slaapcoach'
  | 'voedingsdeskundige'
  | 'stresscoach'
  | 'mindset_coach'
  | 'mindfulness_trainer'
  | 'preventiecoach'
  | 'gezondheidscoach'
  | 'reintegratiebegeleider'
  | 'vertrouwenspersoon_integriteit'
  | 'crisis_conflictadviseur'
  | 'compliance_professional'
  | 'hr_strategie_consultant';

export type ExperienceLevel = '0-2' | '3-5' | '6-10' | '11+';

export type ProfessionalCategoryId = 'ld' | 'psa' | 'welzijn' | 'trauma' | 'arbo';

export type ProfessionalFocus = 'organisatie' | 'medewerker' | 'hybride';

export interface ProfessionalRateRange {
  min: number;
  max: number | null; // null = "X+" (no upper bound)
}

export interface ProfessionalTypeExtendedInfo {
  wanneer: string;
  meerwaarde: string;
  nobco?: boolean;
}

// ─── Categories ────────────────────────────────────────────────────────────────

export const PROFESSIONAL_CATEGORIES: Record<ProfessionalCategoryId, {
  label: string;
  shortLabel: string;
  types: ProfessionalType[];
}> = {
  ld: {
    label: 'Ontwikkeling & Groei',
    shortLabel: 'L&D',
    types: [
      'executive_leiderschapscoach',
      'business_coach',
      'teamcoach',
      'agile_performance_coach',
      'ld_business_partner',
      'change_verandermanager',
      'talent_high_potential_coach',
      'ld_adviseur_trainer',
      'loopbaan_inzetbaarheidscoach',
      'spoor2_reintegratiespecialist',
      'mindset_coach',
      'hr_strategie_consultant',
    ],
  },
  psa: {
    label: 'Psychosociale Arbeidsbelasting',
    shortLabel: 'PSA',
    types: [
      'bedrijfspsycholoog',
      'organisatiepsycholoog',
      'arbeids_organisatie_deskundige',
      'psa_adviseur_burnout_coach',
      'vertrouwenspersoon',
      'mediator',
      'conflictcoach',
      'intercultureel_coach',
      'stresscoach',
      'vertrouwenspersoon_integriteit',
      'crisis_conflictadviseur',
      'compliance_professional',
    ],
  },
  welzijn: {
    label: 'Welzijn & Vitaliteit',
    shortLabel: 'Welzijn',
    types: [
      'ergonomisch_adviseur',
      'arbeidsfysiotherapeut',
      'ergotherapeut_reintegratiecoach',
      'vitaliteitsmanager_coach',
      'natuur_wandelcoach',
      'budgetcoach_financieel_welzijn',
      'verslavingscoach',
      'slaapcoach',
      'voedingsdeskundige',
      'mindfulness_trainer',
      'preventiecoach',
      'gezondheidscoach',
    ],
  },
  trauma: {
    label: 'Trauma & Incidenten',
    shortLabel: 'Trauma',
    types: [
      'crisis_trauma_specialist',
      'rouw_verliesbegeleider',
      'bedrijfsmaatschappelijk_werker',
    ],
  },
  arbo: {
    label: 'Arbo, Keuringen & Verzuimregie',
    shortLabel: 'Arbo',
    types: [
      'bedrijfsarts_arboarts',
      'praktijkondersteuner_bedrijfsarts',
      'arboverpleegkundige',
      'arbeidsdeskundige',
      'casemanager_regie_verzuim',
      'casemanager_inzetbaarheid',
      'arbeidshygienist',
      'hogere_veiligheidskundige',
      'reintegratiebegeleider',
    ],
  },
};

export const CANONICAL_PROFESSIONAL_TYPES: ProfessionalType[] = [
  ...PROFESSIONAL_CATEGORIES.ld.types,
  ...PROFESSIONAL_CATEGORIES.psa.types,
  ...PROFESSIONAL_CATEGORIES.welzijn.types,
  ...PROFESSIONAL_CATEGORIES.trauma.types,
  ...PROFESSIONAL_CATEGORIES.arbo.types,
];

// ─── Focus ─────────────────────────────────────────────────────────────────────

export const PROFESSIONAL_FOCUS: Partial<Record<ProfessionalType, ProfessionalFocus>> = {
  executive_leiderschapscoach: 'organisatie',
  business_coach: 'organisatie',
  teamcoach: 'organisatie',
  agile_performance_coach: 'organisatie',
  ld_business_partner: 'organisatie',
  change_verandermanager: 'organisatie',
  supervisor: 'organisatie',
  ergonomisch_adviseur: 'organisatie',
  organisatiepsycholoog: 'organisatie',
  arbeidsdeskundige: 'organisatie',
  arbeidshygienist: 'organisatie',
  hogere_veiligheidskundige: 'organisatie',
  arbeids_organisatie_deskundige: 'organisatie',
  compliance_professional: 'organisatie',
  hr_strategie_consultant: 'organisatie',
  bedrijfsarts_arboarts: 'hybride',
  casemanager_regie_verzuim: 'hybride',
  arboverpleegkundige: 'hybride',
  praktijkondersteuner_bedrijfsarts: 'hybride',
  crisis_trauma_specialist: 'medewerker',
  mediator: 'medewerker',
  vertrouwenspersoon: 'medewerker',
  intercultureel_coach: 'medewerker',
  psa_adviseur_burnout_coach: 'medewerker',
  bedrijfspsycholoog: 'medewerker',
  bedrijfspsycholoog_preventief: 'medewerker',
  verslavingscoach: 'medewerker',
  vitaliteitsmanager_coach: 'medewerker',
  rouw_verliesbegeleider: 'medewerker',
  talent_high_potential_coach: 'medewerker',
  loopbaan_inzetbaarheidscoach: 'medewerker',
  budgetcoach_financieel_welzijn: 'medewerker',
  bedrijfsmaatschappelijk_werker: 'medewerker',
  natuur_wandelcoach: 'medewerker',
  ergotherapeut_reintegratiecoach: 'medewerker',
  arbeidsfysiotherapeut: 'medewerker',
  slaapcoach: 'medewerker',
  voedingsdeskundige: 'medewerker',
  stresscoach: 'medewerker',
  mindset_coach: 'medewerker',
  mindfulness_trainer: 'medewerker',
  preventiecoach: 'medewerker',
  gezondheidscoach: 'medewerker',
  reintegratiebegeleider: 'medewerker',
  vertrouwenspersoon_integriteit: 'medewerker',
  crisis_conflictadviseur: 'medewerker',
  ld_adviseur_trainer: 'medewerker',
  casemanager_inzetbaarheid: 'medewerker',
  conflictcoach: 'medewerker',
  spoor2_reintegratiespecialist: 'medewerker',
  // Legacy
  loopbaancoach: 'medewerker',
  vitaliteitscoach: 'medewerker',
  burnout_coach: 'medewerker',
  financieel_coach: 'medewerker',
  psycholoog_coach: 'organisatie',
};

// ─── Medewerker/Organisatie-verdeling (facturatie-routering) ──────────────────
// In sync met src/types/professionalRates.ts (server) en de DB-tabel
// `disciplines` (migraties 113 en 172). Medewerkers-Schil (29) = uur-op-uur agenda;
// Organisatie-Schil (18) = HR-Cockpit project-pools via de Milestone-Grendel.

export type BillingRoute = 'agenda_uur' | 'milestone';

export const MEDEWERKER_GERICHTE_TYPES: ProfessionalType[] = [
  'bedrijfsarts_arboarts',
  'praktijkondersteuner_bedrijfsarts',
  'arboverpleegkundige',
  'bedrijfspsycholoog',
  'psa_adviseur_burnout_coach',
  'arbeidsfysiotherapeut',
  'ergotherapeut_reintegratiecoach',
  'vitaliteitsmanager_coach',
  'natuur_wandelcoach',
  'verslavingscoach',
  'arbeidsdeskundige',
  'spoor2_reintegratiespecialist',
  'loopbaan_inzetbaarheidscoach',
  'budgetcoach_financieel_welzijn',
  'talent_high_potential_coach',
  'vertrouwenspersoon',
  'mediator',
  'conflictcoach',
  'crisis_trauma_specialist',
  'rouw_verliesbegeleider',
  'slaapcoach',
  'voedingsdeskundige',
  'stresscoach',
  'mindset_coach',
  'mindfulness_trainer',
  'preventiecoach',
  'gezondheidscoach',
  'reintegratiebegeleider',
  'vertrouwenspersoon_integriteit',
  'crisis_conflictadviseur',
];

export const ORGANISATIE_GERICHTE_TYPES: ProfessionalType[] = [
  'casemanager_regie_verzuim',
  'casemanager_inzetbaarheid',
  'arbeidshygienist',
  'hogere_veiligheidskundige',
  'ergonomisch_adviseur',
  'organisatiepsycholoog',
  'arbeids_organisatie_deskundige',
  'bedrijfsmaatschappelijk_werker',
  'intercultureel_coach',
  'ld_business_partner',
  'ld_adviseur_trainer',
  'executive_leiderschapscoach',
  'business_coach',
  'teamcoach',
  'agile_performance_coach',
  'change_verandermanager',
  'compliance_professional',
  'hr_strategie_consultant',
];

export function isMedewerkerGericht(type: ProfessionalType): boolean {
  return MEDEWERKER_GERICHTE_TYPES.includes(type);
}

export function getBillingRoute(type: ProfessionalType): BillingRoute {
  return isMedewerkerGericht(type) ? 'agenda_uur' : 'milestone';
}

// ─── Labels ────────────────────────────────────────────────────────────────────

export const PROFESSIONAL_TYPE_LABELS: Record<ProfessionalType, string> = {
  // Legacy
  loopbaancoach: 'Loopbaancoach',
  vitaliteitscoach: 'Vitaliteitscoach',
  burnout_coach: 'Burn-out Coach',
  financieel_coach: 'Financieel Coach / Budgetcoach',
  psycholoog_coach: 'Psycholoog-Coach (GZ/Organisatie)',
  // Cat 1
  executive_leiderschapscoach: 'Executive / Leiderschapscoach',
  business_coach: 'Business Coach',
  teamcoach: 'Teamcoach',
  agile_performance_coach: 'Agile / Performance Coach',
  ld_business_partner: 'L&D Business Partner',
  change_verandermanager: 'Change / Verandermanager',
  supervisor: 'Supervisor',
  talent_high_potential_coach: 'Talent / High Potential Coach',
  ld_adviseur_trainer: 'L&D Adviseur / Trainer',
  // Cat 2
  mediator: 'Mediator',
  vertrouwenspersoon: 'Vertrouwenspersoon',
  intercultureel_coach: 'Intercultureel Coach',
  psa_adviseur_burnout_coach: 'PSA Adviseur / Burn-out Coach',
  casemanager_inzetbaarheid: 'Casemanager Inzetbaarheid',
  casemanager_regie_verzuim: 'Casemanager Regie op Verzuim (CROV)',
  bedrijfspsycholoog: 'Bedrijfspsycholoog',
  bedrijfsarts_arboarts: 'Gecertificeerde Bedrijfsarts / Arbo-arts',
  // Cat 3
  organisatiepsycholoog: 'Organisatiepsycholoog',
  vitaliteitsmanager_coach: 'Vitaliteitsmanager / Coach',
  loopbaan_inzetbaarheidscoach: 'Loopbaan / Inzetbaarheidscoach',
  budgetcoach_financieel_welzijn: 'Budgetcoach / Financieel Welzijn',
  natuur_wandelcoach: 'Natuur- / Wandelcoach',
  ergonomisch_adviseur: 'Ergonomisch Adviseur',
  bedrijfspsycholoog_preventief: 'Bedrijfspsycholoog (Preventief)',
  verslavingscoach: 'Verslavingscoach / -deskundige',
  arbeidshygienist: 'Gecertificeerd Arbeidshygiënist',
  arboverpleegkundige: 'Arboverpleegkundige',
  ergotherapeut_reintegratiecoach: 'Ergotherapeut / Re-integratiecoach',
  // Cat 4
  crisis_trauma_specialist: 'Crisis & Trauma Specialist',
  rouw_verliesbegeleider: 'Rouw- en Verliesbegeleider',
  bedrijfsmaatschappelijk_werker: 'Bedrijfsmaatschappelijk Werker',
  // Cat 1 (aanvulling)
  arbeidsdeskundige: 'Arbeidsdeskundige',
  spoor2_reintegratiespecialist: 'Spoor 2 Re-integratiespecialist',
  // Cat 2 (aanvulling)
  conflictcoach: 'Conflictcoach',
  // Cat 5: Arbo
  praktijkondersteuner_bedrijfsarts: 'Praktijkondersteuner Bedrijfsarts (POB)',
  hogere_veiligheidskundige: 'Hogere Veiligheidskundige (HVK)',
  // Cat 3 (aanvulling)
  arbeidsfysiotherapeut: 'Arbeidsfysiotherapeut',
  // Cat 2 (aanvulling)
  arbeids_organisatie_deskundige: 'Arbeids- & Organisatiedeskundige (A&O)',
  // Uitbreiding registerblauwdruk (migratie 172)
  slaapcoach: 'Slaapcoach / Nachtdienst-specialist',
  voedingsdeskundige: 'Voedingsdeskundige',
  stresscoach: 'Stresscoach',
  mindset_coach: 'Mindset Coach',
  mindfulness_trainer: 'Personal Mindfulness Trainer',
  preventiecoach: 'Preventiecoach',
  gezondheidscoach: 'Gezondheids- & Welzijnscoach',
  reintegratiebegeleider: 'Re-integratiebegeleider / Sociaal Werker',
  vertrouwenspersoon_integriteit: 'Vertrouwenspersoon Integriteit',
  crisis_conflictadviseur: 'Crisis- & Conflictadviseur',
  compliance_professional: 'Gecertificeerde Compliance Professional (CCP)',
  hr_strategie_consultant: 'HR-Strategie Consultant',
};

// ─── Short descriptions ────────────────────────────────────────────────────────

export const PROFESSIONAL_TYPE_DESCRIPTIONS: Record<ProfessionalType, string> = {
  loopbaancoach: 'Begeleiding bij carrièreontwikkeling, outplacement, interne mobiliteit.',
  vitaliteitscoach: 'Gericht op energie, leefstijl, balans werk-privé.',
  burnout_coach: 'Gespecialiseerd in stressreductie, burn-outpreventie en hersteltrajecten.',
  financieel_coach: 'Voor zzp\'ers of medewerkers met financiële stress of schulden.',
  psycholoog_coach: 'Inzetbaar bij mentale gezondheid, verzuimpreventie en leiderschap.',
  executive_leiderschapscoach: 'Begeleidt directie en management bij persoonlijk leiderschap, visievorming en cultuur.',
  business_coach: 'Helpt managers en interne ondernemers bij strategische groei en business development.',
  teamcoach: 'Optimaliseert de onderlinge samenwerking, communicatie en patronen binnen complete teams.',
  agile_performance_coach: 'Helpt teams effectiever, wendbaarder en resultaatgerichter samen te werken.',
  ld_business_partner: 'Vertaalt strategische afdelingsdoelen direct naar concrete, organisatiebrede leertrajecten.',
  change_verandermanager: 'Begeleidt de organisatie en haar structuren door reorganisaties en grote transities.',
  supervisor: 'Begeleidt interne coaches en HR-professionals bij reflectie op hun professionele rol.',
  talent_high_potential_coach: 'Richt zich specifiek op de versnelde persoonlijke groei en loopbaan van jonge talenten.',
  ld_adviseur_trainer: 'Geeft praktijkgerichte vaardigheidstrainingen direct aan medewerkers op de werkvloer.',
  mediator: 'Lost formele arbeidsconflicten op tussen medewerkers of management.',
  vertrouwenspersoon: 'Veilige, laagdrempelige opvang bij ongewenst gedrag of integriteitsschendingen.',
  intercultureel_coach: 'Begeleidt medewerkers en teams bij diversiteitsvraagstukken en inclusieve samenwerking.',
  psa_adviseur_burnout_coach: 'Herkent stresssignalen en begeleidt overbelaste medewerkers bij herstel en re-integratie.',
  casemanager_inzetbaarheid: 'Begeleidt re-integratie conform de Wet Verbetering Poortwachter.',
  casemanager_regie_verzuim: 'Regisseert het volledige verzuimproces, bewaakt de deadlines van de Wet verbetering Poortwachter, onderhoudt het contact met alle partijen en vertaalt het advies van de bedrijfsarts naar concrete acties.',
  bedrijfspsycholoog: 'Professionele psychologische begeleiding bij mentale klachten, werkstress en dreigend verzuim.',
  bedrijfsarts_arboarts: 'Beoordeelt de medische belastbaarheid van de medewerker, bewaakt het medisch geheim en adviseert over re-integratiemogelijkheden.',
  organisatiepsycholoog: 'Analyseert cultuur en groepsdynamiek voor een gezonde en productieve werkomgeving.',
  vitaliteitsmanager_coach: 'Coacht medewerkers proactief op persoonlijke energie, leefstijl, vitaliteit en duurzame inzetbaarheid.',
  loopbaan_inzetbaarheidscoach: 'Begeleidt bij re-integratie, interne mobiliteit en duurzame loopbaankeuzes.',
  budgetcoach_financieel_welzijn: 'Ondersteunt medewerkers bij financiële stress, schuldenpreventie en budgettering.',
  natuur_wandelcoach: 'Zet de natuur methodisch in voor stressreductie, reflectie en mentale veerkracht.',
  ergonomisch_adviseur: 'Optimaliseert de (thuis)werkplek ter preventie van fysieke klachten en als onderdeel van Arbobeleid.',
  bedrijfspsycholoog_preventief: 'Proactieve psychologische begeleiding gericht op het versterken van de mentale veerkracht en persoonlijke effectiviteit.',
  verslavingscoach: 'Biedt discrete, gespecialiseerde begeleiding bij afhankelijkheid van middelen (alcohol, drugs, medicijnen) of gedrag (gokken, gaming).',
  arbeidshygienist: 'Onderzoekt en meet omgevingsfactoren op de werkvloer zoals lawaai, ventilatie, verlichting, ergonomie en gevaarlijke stoffen.',
  arboverpleegkundige: 'Werkt nauw samen met de bedrijfsarts, voert sociaal-medische intakes en spreekuren uit, analyseert verzuimoorzaken en adviseert over leefstijl, preventie en werkaanpassingen.',
  ergotherapeut_reintegratiecoach: 'Begeleidt medewerkers praktisch bij fysieke belastbaarheid, werkaanpassingen en duurzame werkhervatting na uitval.',
  crisis_trauma_specialist: 'Directe psychologische opvang na schokkende incidenten op de werkvloer.',
  rouw_verliesbegeleider: 'Ondersteunt teams bij overlijden van een collega of zwaar privéverlies.',
  bedrijfsmaatschappelijk_werker: 'Psychosociale hulp bij problemen op het grensgebied van werk en privé.',
  arbeidsdeskundige: 'Weegt de medische belastbaarheid af tegen de feitelijke functie-eisen en onderzoekt de opties voor Spoor 1 (intern) of Spoor 2 (extern).',
  spoor2_reintegratiespecialist: 'Begeleidt medewerkers vanaf het tweede verzuimjaar (rond week 52) verplicht naar een passende baan buiten de organisatie wanneer intern herstel (Spoor 1) niet haalbaar is.',
  conflictcoach: 'Wordt ingezet bij beginnende of diepgewortelde spanningen op de werkvloer om escalatie naar een formeel, kostbaar juridisch mediationtraject te voorkomen.',
  praktijkondersteuner_bedrijfsarts: 'Voert in taakdelegatie van de bedrijfsarts de reguliere, laagdrempelige verzuimbegeleiding en spreekuren uit, zodat de bedrijfsarts alleen wordt ingezet waar dat medisch noodzakelijk is.',
  hogere_veiligheidskundige: 'Gecertificeerde kerndeskundige die de Risico-Inventarisatie & Evaluatie (RI&E) toetst en certificeert en adviseert over fysieke veiligheid op de werkvloer.',
  arbeidsfysiotherapeut: 'Behandelt klachten aan het bewegingsapparaat met fysieke therapie die direct is gericht op verantwoorde en duurzame werkhervatting.',
  arbeids_organisatie_deskundige: 'Kerndeskundige die het directiebeleid rondom stresspreventie, psychosociale arbeidsbelasting en organisatie-inrichting toetst en certificeert.',
  slaapcoach: 'Begeleidt medewerkers met slaapproblemen en ploeg- of nachtdiensten naar een gezond slaapritme, beter herstel en duurzame belastbaarheid.',
  voedingsdeskundige: 'Adviseert medewerkers over gezonde, werkbare voedingspatronen ter ondersteuning van energie, vitaliteit en de preventie van leefstijlgerelateerde klachten.',
  stresscoach: 'Helpt medewerkers stresssignalen vroegtijdig te herkennen en effectieve copingstrategieën te ontwikkelen, vóórdat spanning omslaat in uitval.',
  mindset_coach: 'Versterkt het denkpatroon, zelfvertrouwen en de veerkracht van medewerkers, zodat zij belemmerende overtuigingen ombuigen naar groei.',
  mindfulness_trainer: 'Leert medewerkers via individuele mindfulnesstraining aandacht, ontspanning en emotieregulatie toe te passen in de dagelijkse werkpraktijk.',
  preventiecoach: 'Signaleert vroege gezondheids- en verzuimrisico\'s bij medewerkers en begeleidt hen met gerichte, preventieve interventies.',
  gezondheidscoach: 'Begeleidt medewerkers integraal bij leefstijl, beweging, ontspanning en welzijn als basis voor duurzame inzetbaarheid.',
  reintegratiebegeleider: 'Begeleidt medewerkers praktisch en sociaal bij werkhervatting en pakt werk- en privéfactoren aan die de re-integratie belemmeren.',
  vertrouwenspersoon_integriteit: 'Onafhankelijk aanspreekpunt voor medewerkers die een (vermoeden van een) integriteitsschending of misstand willen melden, conform de Wet bescherming klokkenluiders.',
  crisis_conflictadviseur: 'Adviseur die bij acute crises en escalerende conflicten op de werkvloer de-escaleert, betrokkenen vertrouwelijk opvangt en de organisatie adviseert over herstel van veiligheid en samenwerking.',
  compliance_professional: 'Gecertificeerde professional die het integriteits- en compliancebeleid van de organisatie op projectbasis inricht, toetst en borgt.',
  hr_strategie_consultant: 'Vertaalt de organisatiestrategie naar een toekomstbestendig HR-beleid op het gebied van strategische personeelsplanning, organisatie-inrichting en duurzame inzetbaarheid.',
};

// ─── Extended info ─────────────────────────────────────────────────────────────

export const PROFESSIONAL_TYPE_EXTENDED: Partial<Record<ProfessionalType, ProfessionalTypeExtendedInfo>> = {
  executive_leiderschapscoach: {
    nobco: true,
    wanneer: 'Bij strategische koerswijzigingen, leiderschapsvraagstukken of persoonlijke ontwikkeling van directie en senior management.',
    meerwaarde: 'Versterkt het strategisch vermogen en de impact van uw sleutelfiguren, wat zorgt voor effectiever bestuur en een stevige organisatiecultuur.',
  },
  business_coach: {
    nobco: true,
    wanneer: 'Voor managers en interne ondernemers die afdelingen of businessunits naar een hoger niveau van groei en efficiëntie moeten loodsen.',
    meerwaarde: 'Versnelt de realisatie van uw businessdoelen, optimaliseert bedrijfsprocessen en vergroot het commerciële en operationele succes.',
  },
  organisatiepsycholoog: {
    wanneer: 'Bij hardnekkige cultuurproblemen, structurele onderstroom op de werkvloer of het herontwerpen van een gezonde werkomgeving.',
    meerwaarde: 'Levert diepgaand inzicht in groepsdynamiek en gedrag, waardoor u verzuimrisico\'s bij de bron aanpakt en de organisatiecultuur structureel verbetert.',
  },
  teamcoach: {
    nobco: true,
    wanneer: 'Bij vastgelopen samenwerking, onderling wantrouwen, eilandjescultuur of de start van een nieuw (management)team.',
    meerwaarde: 'Doorbreekt ineffectieve patronen en transformeert groepen naar high-performing teams die open communiceren en betere resultaten leveren.',
  },
  agile_performance_coach: {
    wanneer: 'Wanneer teams sneller moeten inspelen op marktveranderingen, de productiviteit achterblijft of processen te bureaucratisch zijn.',
    meerwaarde: 'Richt een cultuur van continu verbeteren in, waardoor de time-to-market verkort en teams zelfsturend en wendbaar worden.',
  },
  ld_business_partner: {
    wanneer: 'Om strategische organisatiedoelen (zoals digitalisering of schaalgrootte) te vertalen naar de benodigde vaardigheden op de vloer.',
    meerwaarde: 'Zorgt dat uw opleidingsbudget direct bijdraagt aan de businessstrategie en dat uw menselijk kapitaal toekomstbestendig blijft.',
  },
  change_verandermanager: {
    wanneer: 'Tijdens ingrijpende transities, fusies of reorganisaties waarbij de menskant intensieve begeleiding vraagt.',
    meerwaarde: 'Minimaliseert de productiviteitsdip tijdens veranderingen, verlaagt de weerstand op de vloer en borgt een soepele overgang naar de nieuwe situatie.',
  },
  supervisor: {
    nobco: true,
    wanneer: 'Voor de professionele kwaliteitsborging, ethische reflectie en intervisie van uw interne coaches, HR-professionals of vertrouwenspersonen.',
    meerwaarde: 'Voorkomt professionele blindheid en burn-out onder uw interne sleutelfiguren, waardoor de interne begeleidingskwaliteit hoog blijft.',
  },
  ergonomisch_adviseur: {
    wanneer: 'Bij een hoog percentage fysieke klachten (RSI/KANS), bij de inrichting van een nieuw kantoorpand of voor beleid rondom gezond thuiswerken.',
    meerwaarde: 'Voorkomt kostbaar fysiek verzuim door werkplekken optimaal in te richten en medewerkers bewust te maken van een gezonde werkhouding.',
  },
  crisis_trauma_specialist: {
    wanneer: 'Direct na een schokkende gebeurtenis op de werkvloer, zoals een ernstig bedrijfsongeval, overval, agressie-incident of suïcide.',
    meerwaarde: 'Biedt acute, specialistische opvang die de kans op PTSS en langdurige psychische uitval bij medewerkers drastisch verkleint.',
  },
  mediator: {
    wanneer: 'Bij een dreigend of reeds geëscaleerd arbeidsconflict tussen collega\'s of tussen een medewerker en leidinggevende.',
    meerwaarde: 'Voorkomt kostbare juridische procedures en verzuim door in een veilige setting te bouwen aan een gedragen, duurzame oplossing.',
  },
  vertrouwenspersoon: {
    wanneer: 'Als laagdrempelig en wettelijk verplicht aanspreekpunt voor medewerkers die te maken krijgen met ongewenst gedrag of integriteitsschendingen.',
    meerwaarde: 'Biedt een vroege radar voor misstanden, beschermt de sociale veiligheid en voorkomt dat issues escaleren naar de media of de rechter.',
  },
  intercultureel_coach: {
    nobco: true,
    wanneer: 'Bij de begeleiding van expats, de integratie van internationale talenten of het overbruggen van cultuurverschillen op de werkvloer.',
    meerwaarde: 'Versnelt de onboarding van buitenlands talent, versterkt de inclusie en voorkomt dat cultuurbarsers leiden tot vroegtijdig vertrek of conflicten.',
  },
  psa_adviseur_burnout_coach: {
    nobco: true,
    wanneer: 'Bij medewerkers die chronische stresssignalen vertonen, of die na uitval wegens een burn-out duurzaam moeten re-integreren.',
    meerwaarde: 'Verkort de verzuimduur aanzienlijk en voorkomt terugval door de medewerker effectieve copingmechanismen en grenzen aan te leren.',
  },
  vitaliteitsmanager_coach: {
    nobco: true,
    wanneer: 'Als proactieve interventie om medewerkers te helpen bij het verbeteren van hun energiebalans, leefstijl, slaap en mentale veerkracht.',
    meerwaarde: 'Verhoogt de algehele inzetbaarheid en productiviteit op de werkvloer en bouwt aan een cultuur van preventieve gezondheid.',
  },
  rouw_verliesbegeleider: {
    wanneer: 'Bij het overlijden van een collega, of wanneer een medewerker te maken krijgt met een ingrijpend privéverlies (zoals partner of kind).',
    meerwaarde: 'Begeleidt de medewerker én het team door het rouwproces, wat zorgt voor een menselijke organisatie en voorkomt dat rouw omslaat in langdurig ziekteverzuim.',
  },
  talent_high_potential_coach: {
    nobco: true,
    wanneer: 'Voor de versnelde ontwikkeling, motivatie en binding van uw meest veelbelovende (jonge) talenten binnen de organisatie.',
    meerwaarde: 'Voorkomt de uitstroom van schaars talent naar de concurrentie en vult uw interne successieplanning tijdig aan met sterke opvolgers.',
  },
  loopbaan_inzetbaarheidscoach: {
    nobco: true,
    wanneer: 'Bij mobiliteitsvraagstukken, heroriëntatie (2e spoor re-integratie), functiewijzigingen of wanneer een medewerker vastloopt in zijn huidige rol.',
    meerwaarde: 'Zorgt dat medewerkers proactief de regie nemen over hun loopbaan, waardoor zij sneller doorstromen naar een plek waar zij wél renderen.',
  },
  budgetcoach_financieel_welzijn: {
    wanneer: 'Bij medewerkers met loonbeslagen, frequent kortverzuim door geldzorgen, of die door inflatie/privésituaties vastlopen in hun administratie.',
    meerwaarde: 'Neemt de immense mentale druk van financiële stress weg, wat direct leidt tot minder verzuim, betere concentratie en lagere administratieve lasten voor HR.',
  },
  bedrijfsmaatschappelijk_werker: {
    wanneer: 'Wanneer psychosociale problemen op het snijvlak van werk en privé (zoals een vechtscheiding of mantelzorg) de inzetbaarheid bedreigen.',
    meerwaarde: 'Biedt praktische en therapeutische hulp in de breedte, waardoor de medewerker de grip op zijn leven behoudt en stabiel inzetbaar blijft voor het werk.',
  },
  natuur_wandelcoach: {
    nobco: true,
    wanneer: 'Voor medewerkers die vastzitten in hun hoofd, milde stressklachten ervaren of voor wie traditionele praatcoaching op kantoor te drempelig is.',
    meerwaarde: 'Maakt gebruik van de bewezen stressverlagende effecten van de natuur, wat leidt tot snellere inzichten, diepere ontspanning en een kortere hersteltijd.',
  },
  ld_adviseur_trainer: {
    wanneer: 'Voor het ontwerpen en klassikaal of individueel uitvoeren van praktische vaardigheidstrainingen (zoals timemanagement of communicatie).',
    meerwaarde: 'Zorgt voor een directe, meetbare upgrade van de competenties op de werkvloer, waardoor teams efficiënter en professioneler opereren.',
  },
  casemanager_inzetbaarheid: {
    wanneer: 'Bij langdurig verzuim of re-integratievraagstukken waarbij een centrale regie op het dossier noodzakelijk is.',
    meerwaarde: 'Borgt een correcte uitvoering van de Wet Verbetering Poortwachter en voorkomt sancties bij het UWV door tijdige en aantoonbare re-integratieactiviteiten.',
  },
  casemanager_regie_verzuim: {
    wanneer: 'Vanaf de eerste verzuimdag (of uiterlijk in week 1) voor de procesbewaking, bij de opzet van het Plan van Aanpak in week 8, en als vast aanspreekpunt tijdens het gehele re-integratietraject.',
    meerwaarde: 'Ontlast HR en management volledig in de complexe verzuimadministratie, minimaliseert de kans op UWV-sancties door strikte procesbewaking en houdt de vaart in de communicatie tussen werkgever en werknemer.',
  },
  bedrijfspsycholoog: {
    wanneer: 'Als een medewerker te maken heeft met overspannenheid, burn-outklachten, angst of somberheid die het dagelijks functioneren en de inzetbaarheid op het werk belemmeren.',
    meerwaarde: 'Zorgt voor een snellere en duurzame re-integratie, voorkomt langdurig psychisch verzuim en versterkt de mentale veerkracht binnen uw teams.',
  },
  bedrijfspsycholoog_preventief: {
    wanneer: 'Als u medewerkers preventief wilt ondersteunen bij het bewaken van de werk-privébalans, het vergroten van de mentale energie en het effectief omgaan met vroege stresssignalen, nog vóórdat er sprake is van uitval.',
    meerwaarde: 'Verhoogt het werkgeluk en de vitaliteit op de werkvloer, stimuleert duurzame inzetbaarheid en voorkomt proactief dat lichte klachten uitgroeien tot langdurig verzuim.',
  },
  bedrijfsarts_arboarts: {
    wanneer: 'Uiterlijk in week 6 van het verzuim (verplichte Probleemanalyse), bij vastgelopen re-integratie of preventief via een open spreekuur.',
    meerwaarde: 'Voorkomt kostbare loonsancties van het UWV (Poortwachter), versnelt de terugkeer naar de werkvloer en geeft HR concrete, functionele kaders voor werkhervatting.',
  },
  verslavingscoach: {
    wanneer: 'Preventief wanneer een medewerker anoniem hulp zoekt, bij prestatie- of gedragsveranderingen op de vloer, of als nazorg na een kliniektraject.',
    meerwaarde: 'Grijpt in vóórdat problematiek escaleert in zwaar ziekteverzuim. De volledige anonimiteit verlaagt de drempel voor de medewerker drastisch.',
  },
  arbeidsdeskundige: {
    wanneer: 'Rond week 42 tot 52 van het verzuim voor het verplichte Arbeidsdeskundig Onderzoek (ADO), of bij blijvende functionele beperkingen.',
    meerwaarde: 'Levert het cruciale, juridisch sluitende rapport voor het UWV-dossier en schept absolute duidelijkheid over de te varen koers in het verzuimtraject.',
  },
  arbeidshygienist: {
    wanneer: 'Bij het opstellen of toetsen van de RI&E, bij collectieve gezondheidsklachten (bijv. hoofdpijn op kantoor) of bij de inrichting van nieuwe werkplekken.',
    meerwaarde: 'Zorgt voor collectieve preventie voor de gehele afdeling, waarborgt compliance met de Arbeidsinspectie en voorkomt kostbare beroepsziekten.',
  },
  arboverpleegkundige: {
    wanneer: 'In de vroege fasen van verzuim (vóór week 6) voor snelle triage, bij lichte medische of vitaliteitsvragen, of preventief tijdens open spreekuren om uitval te voorkomen.',
    meerwaarde: 'Werkt drempelverlagend voor de medewerker door kortere wachttijden dan bij de bedrijfsarts, biedt direct praktische vitaliteitsadviezen en bespaart kosten door de bedrijfsarts alleen in te zetten wanneer dat medisch noodzakelijk is.',
  },
  praktijkondersteuner_bedrijfsarts: {
    wanneer: 'Voor de reguliere, laagdrempelige verzuimbegeleiding in taakdelegatie: verzuimspreekuren, vervolgcontacten en dossiervoering onder supervisie van de bedrijfsarts.',
    meerwaarde: 'Verlaagt de verzuimkosten aanzienlijk doordat het duurdere artsentarief alleen wordt ingezet waar medisch noodzakelijk, terwijl de doorlooptijd van spreekuren kort blijft.',
  },
  spoor2_reintegratiespecialist: {
    wanneer: 'Vanaf (uiterlijk) het tweede verzuimjaar — rond week 52, na de Eerstejaarsevaluatie — wanneer terugkeer binnen de eigen organisatie niet meer realistisch is.',
    meerwaarde: 'Borgt een Poortwachter-proof Spoor 2-traject richting het UWV en vergroot de kans op duurzame plaatsing buiten de organisatie, wat kostbare loonsancties voorkomt.',
  },
  conflictcoach: {
    wanneer: 'Bij beginnende wrijving of sluimerende spanningen tussen collega\'s of met een leidinggevende, vóórdat het conflict formeel escaleert.',
    meerwaarde: 'Voorkomt kostbare mediation- of juridische trajecten en conflictgerelateerd verzuim door spanningen vroegtijdig bespreekbaar en hanteerbaar te maken.',
  },
  hogere_veiligheidskundige: {
    wanneer: 'Bij het toetsen en certificeren van de RI&E, na (bijna-)ongevallen of bij complexe veiligheidsvraagstukken in productie-, bouw- of laboratoriumomgevingen.',
    meerwaarde: 'Waarborgt als gecertificeerd kerndeskundige de wettelijk verplichte RI&E-toetsing, voorkomt boetes van de Arbeidsinspectie en verkleint de kans op ernstige bedrijfsongevallen.',
  },
  slaapcoach: {
    nobco: true,
    wanneer: 'Bij medewerkers met aanhoudende slaapklachten, vermoeidheid door ploeg- of nachtdiensten, of wanneer slaaptekort de concentratie en veiligheid op de werkvloer bedreigt.',
    meerwaarde: 'Herstelt de basis van mentale en fysieke belastbaarheid, verlaagt het risico op fouten en ongevallen en voorkomt dat slaaptekort uitgroeit tot verzuim.',
  },
  voedingsdeskundige: {
    wanneer: 'Bij energiedips, onregelmatige werktijden, overgewicht of leefstijlgerelateerde gezondheidsrisico\'s die de inzetbaarheid beïnvloeden.',
    meerwaarde: 'Versterkt energie en concentratie op de werkvloer en verkleint op termijn het risico op leefstijlgerelateerd verzuim.',
  },
  stresscoach: {
    nobco: true,
    wanneer: 'Bij medewerkers die toenemende werkdruk, spanning of piekergedrag ervaren, nog vóórdat er sprake is van overspannenheid of burn-out.',
    meerwaarde: 'Grijpt preventief in op psychosociale arbeidsbelasting, waardoor medewerkers inzetbaar blijven en kostbaar psychisch verzuim wordt voorkomen.',
  },
  mindset_coach: {
    nobco: true,
    wanneer: 'Wanneer medewerkers vastlopen door faalangst, onzekerheid of belemmerende overtuigingen die hun ontwikkeling en prestaties remmen.',
    meerwaarde: 'Ontgrendelt onbenut potentieel, vergroot het eigenaarschap van medewerkers en versnelt hun persoonlijke en professionele groei.',
  },
  mindfulness_trainer: {
    wanneer: 'Bij medewerkers met stress, concentratieproblemen of een hoog prikkelniveau die behoefte hebben aan praktische tools voor rust en focus.',
    meerwaarde: 'Verlaagt aantoonbaar het stressniveau, verbetert de focus en versterkt de mentale veerkracht van medewerkers.',
  },
  preventiecoach: {
    nobco: true,
    wanneer: 'Bij een verhoogd kortverzuim, na een PMO met aandachtspunten of wanneer medewerkers vroege signalen van overbelasting vertonen.',
    meerwaarde: 'Voorkomt dat lichte klachten uitgroeien tot langdurig verzuim en versterkt de duurzame inzetbaarheid van medewerkers.',
  },
  gezondheidscoach: {
    nobco: true,
    wanneer: 'Wanneer medewerkers hun leefstijl en algehele gezondheid willen verbeteren, of wanneer welzijnsklachten het functioneren beginnen te beïnvloeden.',
    meerwaarde: 'Bouwt aan een gezonde, energieke werkvloer en verlaagt het verzuimrisico door medewerkers duurzaam regie te laten nemen over hun welzijn.',
  },
  reintegratiebegeleider: {
    wanneer: 'Tijdens het re-integratietraject wanneer sociale, praktische of privéomstandigheden de terugkeer naar werk vertragen.',
    meerwaarde: 'Neemt belemmeringen in de breedte weg, versnelt de werkhervatting en ondersteunt een Poortwachter-proof re-integratiedossier.',
  },
  vertrouwenspersoon_integriteit: {
    wanneer: 'Als vertrouwelijk aanspreekpunt bij vermoedens van fraude, corruptie, belangenverstrengeling of andere misstanden binnen de organisatie.',
    meerwaarde: 'Ondersteunt een zorgvuldige meldprocedure conform de Wet bescherming klokkenluiders, beschermt melders en verkleint de kans op reputatieschade.',
  },
  crisis_conflictadviseur: {
    wanneer: 'Bij een acuut incident, een escalerend conflict of een onveilige situatie op de werkvloer waarbij snel en onafhankelijk ingrijpen nodig is.',
    meerwaarde: 'Voorkomt verdere escalatie en verzuim, geeft medewerkers een veilig aanspreekpunt en levert de organisatie concreet herstel- en veiligheidsadvies.',
  },
  compliance_professional: {
    wanneer: 'Bij de inrichting of herziening van een integriteits- of compliancekader, na een incident of ter voorbereiding op een (externe) audit.',
    meerwaarde: 'Borgt aantoonbare naleving van wet- en regelgeving, verkleint juridische en reputatierisico\'s en versterkt de integriteitscultuur.',
  },
  hr_strategie_consultant: {
    wanneer: 'Bij groei, krimp of strategische koerswijzigingen waarbij het HR-beleid en de strategische personeelsplanning opnieuw moeten worden ingericht.',
    meerwaarde: 'Zorgt dat het HR-beleid direct bijdraagt aan de bedrijfsdoelen en dat de organisatie tijdig beschikt over de juiste mensen en competenties.',
  },
};

// ─── Category descriptions ────────────────────────────────────────────────────

export const PROFESSIONAL_CATEGORY_DESCRIPTIONS: Record<ProfessionalCategoryId, string> = {
  ld: 'L&D staat voor Learning & Development (Leren en Ontwikkelen). Hier kunt u professionals vinden die zich richten op het duurzaam verbeteren van de vaardigheden, kennis en prestaties van medewerkers. Dit draagt direct bij aan de groei en het succes van de organisatie.',
  psa: 'PSA staat voor Psychosociale Arbeidsbelasting. Hier kunt u professionals vinden die zich richten op het duurzaam verbeteren van de sociale veiligheid, werkdrukbalans en psychische gezondheid van medewerkers. Dit draagt direct bij aan een veilige werkomgeving, minder verzuim en een gezond bedrijfsklimaat.',
  welzijn: 'Welzijn staat voor het welbevinden en de vitaliteit van uw personeel. Hier kunt u professionals vinden die zich richten op het duurzaam verbeteren van de fysieke en mentale energie, werk-privébalans en het werkgeluk van medewerkers. Dit draagt direct bij aan een hoge medewerkerstevredenheid, meer productiviteit en een optimale bedrijfscultuur.',
  trauma: 'Trauma staat voor de psychische en emotionele impact van ingrijpende of schokkende gebeurtenissen op de werkvloer of in de privé sfeer. Hier kunt u professionals vinden die zich richten op het duurzaam herstellen van de mentale balans, veerkracht en het veiligheidsgevoel van medewerkers na een crisis. Dit draagt direct bij aan een effectieve re-integratie, emotioneel herstel en de duurzame inzetbaarheid van uw team.',
  arbo: 'Arbo staat voor de gecertificeerde kerndeskundigen en procesregisseurs rondom arbeidsomstandigheden, keuringen en verzuimregie. Hier kunt u alle professionals vinden die de Wet verbetering Poortwachter en de Arbowet voor u sluitend maken — van bedrijfsarts en praktijkondersteuner tot arbeidsdeskundige en veiligheidskundige. Dit maakt een traditionele arbodienst overbodig en houdt u volledig compliant.',
};

// ─── Rate ranges ───────────────────────────────────────────────────────────────

export const PROFESSIONAL_RATE_RANGES: Partial<Record<ProfessionalType, Record<ExperienceLevel, ProfessionalRateRange>>> = {
  executive_leiderschapscoach:  { '0-2': { min:100, max:120 }, '3-5': { min:125, max:150 }, '6-10': { min:160, max:190 }, '11+': { min:210, max:null } },
  business_coach:               { '0-2': { min:95,  max:115 }, '3-5': { min:120, max:140 }, '6-10': { min:145, max:175 }, '11+': { min:185, max:null } },
  teamcoach:                    { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:130 }, '6-10': { min:135, max:160 }, '11+': { min:175, max:null } },
  agile_performance_coach:      { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:135, max:155 }, '11+': { min:165, max:null } },
  ld_business_partner:          { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:165, max:null } },
  change_verandermanager:       { '0-2': { min:85,  max:95  }, '3-5': { min:100, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  supervisor:                   { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  talent_high_potential_coach:  { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  ld_adviseur_trainer:          { '0-2': { min:80,  max:90  }, '3-5': { min:95,  max:110 }, '6-10': { min:115, max:135 }, '11+': { min:145, max:null } },
  arbeidsdeskundige:            { '0-2': { min:95,  max:110 }, '3-5': { min:115, max:135 }, '6-10': { min:140, max:165 }, '11+': { min:175, max:null } },
  mediator:                     { '0-2': { min:100, max:120 }, '3-5': { min:125, max:145 }, '6-10': { min:150, max:180 }, '11+': { min:190, max:null } },
  vertrouwenspersoon:           { '0-2': { min:95,  max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:160, max:null } },
  intercultureel_coach:         { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:130, max:155 }, '11+': { min:165, max:null } },
  psa_adviseur_burnout_coach:   { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:130 }, '6-10': { min:135, max:155 }, '11+': { min:170, max:null } },
  casemanager_inzetbaarheid:    { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:150, max:null } },
  casemanager_regie_verzuim:    { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:130 }, '6-10': { min:135, max:155 }, '11+': { min:165, max:null } },
  bedrijfspsycholoog:           { '0-2': { min:95,  max:115 }, '3-5': { min:115, max:135 }, '6-10': { min:135, max:160 }, '11+': { min:160, max:null } },
  bedrijfsarts_arboarts:        { '0-2': { min:110, max:130 }, '3-5': { min:135, max:160 }, '6-10': { min:165, max:195 }, '11+': { min:210, max:null } },
  organisatiepsycholoog:        { '0-2': { min:95,  max:115 }, '3-5': { min:120, max:145 }, '6-10': { min:150, max:175 }, '11+': { min:185, max:null } },
  vitaliteitsmanager_coach:     { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:135, max:155 }, '11+': { min:165, max:null } },
  loopbaan_inzetbaarheidscoach: { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:160, max:null } },
  budgetcoach_financieel_welzijn: { '0-2': { min:80, max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  natuur_wandelcoach:           { '0-2': { min:80,  max:95  }, '3-5': { min:100, max:115 }, '6-10': { min:120, max:140 }, '11+': { min:150, max:null } },
  ergonomisch_adviseur:         { '0-2': { min:80,  max:90  }, '3-5': { min:95,  max:110 }, '6-10': { min:115, max:130 }, '11+': { min:140, max:null } },
  bedrijfspsycholoog_preventief: { '0-2': { min:95, max:115 }, '3-5': { min:120, max:145 }, '6-10': { min:150, max:175 }, '11+': { min:190, max:null } },
  verslavingscoach:             { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:130 }, '6-10': { min:135, max:155 }, '11+': { min:170, max:null } },
  arbeidshygienist:             { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  arboverpleegkundige:          { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  ergotherapeut_reintegratiecoach: { '0-2': { min:85, max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  crisis_trauma_specialist:     { '0-2': { min:100, max:120 }, '3-5': { min:125, max:145 }, '6-10': { min:150, max:180 }, '11+': { min:200, max:null } },
  rouw_verliesbegeleider:       { '0-2': { min:90,  max:110 }, '3-5': { min:115, max:130 }, '6-10': { min:135, max:160 }, '11+': { min:175, max:null } },
  bedrijfsmaatschappelijk_werker: { '0-2': { min:85, max:100 }, '3-5': { min:105, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:160, max:null } },
  spoor2_reintegratiespecialist:  { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:160, max:null } },
  conflictcoach:                  { '0-2': { min:90,  max:105 }, '3-5': { min:110, max:130 }, '6-10': { min:135, max:160 }, '11+': { min:170, max:null } },
  praktijkondersteuner_bedrijfsarts: { '0-2': { min:80, max:95 }, '3-5': { min:100, max:115 }, '6-10': { min:120, max:140 }, '11+': { min:150, max:null } },
  hogere_veiligheidskundige:      { '0-2': { min:95,  max:110 }, '3-5': { min:115, max:135 }, '6-10': { min:140, max:165 }, '11+': { min:175, max:null } },
  arbeidsfysiotherapeut:          { '0-2': { min:85,  max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  arbeids_organisatie_deskundige: { '0-2': { min:95,  max:110 }, '3-5': { min:115, max:135 }, '6-10': { min:140, max:165 }, '11+': { min:175, max:null } },
  slaapcoach: { '0-2': { min:80, max:95 }, '3-5': { min:100, max:115 }, '6-10': { min:120, max:140 }, '11+': { min:150, max:null } },
  voedingsdeskundige: { '0-2': { min:90, max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:135, max:155 }, '11+': { min:165, max:null } },
  stresscoach: { '0-2': { min:90, max:105 }, '3-5': { min:110, max:130 }, '6-10': { min:135, max:155 }, '11+': { min:170, max:null } },
  mindset_coach: { '0-2': { min:85, max:100 }, '3-5': { min:105, max:120 }, '6-10': { min:125, max:145 }, '11+': { min:155, max:null } },
  mindfulness_trainer: { '0-2': { min:80, max:95 }, '3-5': { min:100, max:115 }, '6-10': { min:120, max:140 }, '11+': { min:150, max:null } },
  preventiecoach: { '0-2': { min:90, max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:135, max:155 }, '11+': { min:165, max:null } },
  gezondheidscoach: { '0-2': { min:90, max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:135, max:155 }, '11+': { min:165, max:null } },
  reintegratiebegeleider: { '0-2': { min:85, max:100 }, '3-5': { min:105, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:160, max:null } },
  vertrouwenspersoon_integriteit: { '0-2': { min:95, max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:160, max:null } },
  crisis_conflictadviseur: { '0-2': { min:100, max:120 }, '3-5': { min:125, max:145 }, '6-10': { min:150, max:175 }, '11+': { min:190, max:null } },
  compliance_professional: { '0-2': { min:95, max:110 }, '3-5': { min:115, max:135 }, '6-10': { min:140, max:165 }, '11+': { min:175, max:null } },
  hr_strategie_consultant: { '0-2': { min:90, max:105 }, '3-5': { min:110, max:125 }, '6-10': { min:130, max:150 }, '11+': { min:165, max:null } },
};

export function formatRateRange(range: ProfessionalRateRange): string {
  if (range.max === null) return `€${range.min}+`;
  return `€${range.min} – €${range.max}`;
}

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  '0-2': 'Starter (0-2 jaar ZZP)',
  '3-5': 'Professional (3-5 jaar ZZP)',
  '6-10': 'Senior/Specialist (6-10 jaar ZZP)',
  '11+': 'Expert/Executive (11+ jaar ZZP)',
};
